import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import translations from './translations';
import './UserInfoForm.css';
import axios from 'axios';
import { API_BASE_URL, API_TOKEN, STORAGE_KEYS } from '../config';
import { safeGet, safeSet, safeRemove } from '../utils/storage';
import { getCommonParams } from '../utils/url';
import { getAssessmentConfig } from '../data/registry';
import { assessmentMetadata, responseMetadata, LANGUAGE_OPTIONS, isAdultAssessment,
  normalizeAssessmentLanguage, uiLanguage, addMetadataToParams } from '../utils/assessmentLanguage';
import { persistAssessmentMetadata } from '../utils/adultQuestionBank';
import { resolveSubmissionContext } from '../utils/submissionRecovery';

const introUrl = () => {
  const userInfo = safeGet(STORAGE_KEYS.USER_INFO, {});
  const context = resolveSubmissionContext({ userInfo,
    urlParams: { ...getCommonParams(), useruuid: userInfo.useruuid, evaluation_uuid: userInfo.evaluation_uuid,
      report_language: userInfo.report_language || getCommonParams().report_language },
    activeSession: safeGet(STORAGE_KEYS.ACTIVE_SESSION, {}),
    sessions: safeGet(STORAGE_KEYS.USER_EVALUATION_SESSIONS, {}) });
  const params = addMetadataToParams(new URLSearchParams({ defaultLang: context.defaultLang }), context);
  ['useruuid', 'evaluation_uuid', 'companyCode', 'Job_NID', 'User_NID', 'codeType', 'evaluation_type']
    .forEach(key => { if (context[key]) params.set(key, context[key]); });
  return `/intro?${params.toString()}`;
};

function UserInfoForm({ type = 'adult14_hr' }) {
  const navigate = useNavigate();
  
  // 使用统一的 URL 参数解析工具
  const { 
    useruuid, 
    defaultLang: urlDefaultLang,
    evaluation_uuid, 
    isNewCandidate 
  } = getCommonParams();
  const [answerLanguage, setAnswerLanguage] = useState(() =>
    normalizeAssessmentLanguage(getCommonParams().assessment_language || urlDefaultLang));
  const [reportLanguage, setReportLanguage] = useState(() =>
    normalizeAssessmentLanguage(getCommonParams().report_language, 'zh-hans'));
  const defaultLang = uiLanguage(answerLanguage);
  const languageMetadata = useCallback(() => assessmentMetadata({ ...getCommonParams(), defaultLang,
    assessment_language: answerLanguage, report_language: reportLanguage }), [defaultLang, answerLanguage, reportLanguage]);

  // 初始化时保存测评类型
  useEffect(() => {
    const config = getAssessmentConfig(type);
    // 获取之前的测评类型
    const savedType = safeGet(STORAGE_KEYS.ASSESSMENT_TYPE);
    
    // 如果之前没有类型，或者类型发生了变化，则清理旧数据
    if (savedType && savedType !== config.id) {
      console.log(`Switching assessment type from ${savedType} to ${config.id}. Clearing old data.`);
      safeRemove(STORAGE_KEYS.QUIZ_ANSWERS);
      // 清除所有可能的题目顺序缓存
      ['zh-TW', 'zh-CN', 'en', 'vi', 'id'].forEach(lang => {
        safeRemove(STORAGE_KEYS.QUIZ_ORDER_PREFIX + lang);
        safeRemove(`${STORAGE_KEYS.QUIZ_ORDER_PREFIX}${savedType}_${lang}`);
      });
      safeRemove(STORAGE_KEYS.START_TIME);
      safeRemove(STORAGE_KEYS.TIME_TAKEN);
    }
    
    // 保存新的测评类型
    safeSet(STORAGE_KEYS.ASSESSMENT_TYPE, config.id);
  }, [type, defaultLang]);

  const t = translations[defaultLang] || translations['zh-TW'];
  // 使用 config.js 中的配置
  const accessToken = API_TOKEN;
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  
  // 表单数据状态
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    birthYear: '',
    gender: '',
    ageGroup: '' // 新增年龄段
  });
  
  // 表单验证错误状态
  const [formErrors, setFormErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  
  // 获取当前测评配置
  const assessmentConfig = getAssessmentConfig(type);
  const currentYear = new Date().getFullYear();
  
  // 生成年份选项
  let yearOptions = [];
  if (type === 'intelligent') {
    if (formData.ageGroup) {
      const ageGroupRanges = {
        'kid': { min: 3, max: 6 },
        'elementary': { min: 7, max: 12 },
        'junior': { min: 13, max: 15 },
        'high': { min: 16, max: 18 },
        'university': { min: 19, max: 22 },
        'community': { min: 23, max: 99 }
      };
      const range = ageGroupRanges[formData.ageGroup];
      if (range) {
        for (let y = currentYear - range.min; y >= currentYear - range.max; y--) {
          yearOptions.push(y);
        }
      }
    }
  } else {
    const minAge = assessmentConfig.ageRange?.min || 14;
    const maxAge = assessmentConfig.ageRange?.max || 99;
    for (let y = currentYear - minAge; y >= currentYear - maxAge; y--) {
      yearOptions.push(y);
    }
  }

  useEffect(() => {
    // 在组件初始化时清除localStorage中可能存在的旧的公司相关参数
    const existingUserInfo = safeGet(STORAGE_KEYS.USER_INFO, null);
    if (existingUserInfo) {
      const userInfo = existingUserInfo;
      delete userInfo.companyCode;
      delete userInfo.Job_NID;
      delete userInfo.User_NID;
      delete userInfo.codeType;
      safeSet(STORAGE_KEYS.USER_INFO, userInfo);
    }


    
    async function fetchUserInfo() {
      if (isNewCandidate) {
        setShowForm(true);
        setLoading(false);
        return;
      }

      if (!useruuid) {
        setError(t.userNotFound || 'user not found');
        setLoading(false);
        return;
      }

      // 首先验证 useruuid 是否有效
      try {
        const res = await fetch(`${API_BASE_URL}/api/assessment/userinfo/${useruuid}/`, {
          headers: {
            Authorization: `Bearer ${accessToken}`,
            'Content-Type': 'application/json',
          },
        });
        
        const contentType = res.headers.get('content-type') || '';
        if (res.ok && contentType.includes('application/json')) {
          // useruuid 有效，无论是否有evaluation_uuid都需要检查测评状态
          await checkExistingEvaluation();
        } else {
          // const raw = await res.text();
          await res.text();
          setError(t.userNotFound || '找不到使用者，請確認連結是否正確');
          setLoading(false);
        }
      } catch (err) {
        console.error("fetchUserInfo error:", err);
        setError((t.userNotFound || '連線失敗或使用者不存在') + ' (Error: ' + (err.message || err.toString()) + ')');
        setLoading(false);
      }
    }

    // 检查是否已存在完成的测评记录
    async function checkExistingEvaluation() {
      try {
        // 使用统一工具获取 URL 参数
        const { 
          evaluation_uuid: evaluationUuid, 
          useruuid: userUuid,
          companyCode: companyCode2,
          Job_NID: jobNID2,
          User_NID: userNID2,
          codeType: codeType2,
          evaluation_type: evaluationType2
        } = getCommonParams();
        
        // 只有当有evaluation_uuid和useruuid时才检查测评状态
        if (evaluationUuid && userUuid) {
          const statusResponse = await axios.get(`${API_BASE_URL}/api/assessment/evaluation_status`, {
            params: {
              useruuid: userUuid,
              evaluation_uuid: evaluationUuid
            },
            headers: {
              Authorization: `Bearer ${accessToken}`,
              'Content-Type': 'application/json',
            },
          });
          
          if (statusResponse.status === 200) {
            const status = statusResponse.data.status;
            
            if (status === 'complete') {
              setError('您已经完成过此测评，无法重复进行。如有疑问，请联系管理员。');
              setLoading(false);
              return;
            } else if (status === 'start' || status === 'create') {
              const userInfo = { useruuid, defaultLang, evaluation_uuid,
                ...languageMetadata(), ...responseMetadata(statusResponse.data),
                ...(getCommonParams().report_language ? { report_language: reportLanguage } : {}) };
              safeSet(STORAGE_KEYS.USER_INFO, userInfo);
              safeSet(STORAGE_KEYS.ACTIVE_SESSION, {
                ...userInfo,
                companyCode: companyCode2,
                Job_NID: jobNID2,
                User_NID: userNID2,
                codeType: codeType2,
                evaluation_type: evaluationType2,
                createdAt: new Date().toISOString(),
              });
              const sessions2 = safeGet(STORAGE_KEYS.USER_EVALUATION_SESSIONS, {});
              const orderKey2 = `${STORAGE_KEYS.QUIZ_ORDER_PREFIX}${defaultLang}`;
              let existingAnswers2 = safeGet(STORAGE_KEYS.QUIZ_ANSWERS, {});
              const hasExistingAnswers2 = Object.keys(existingAnswers2).length > 0;
              const prev2 = sessions2[useruuid];
              const changed2 = !!prev2 && (prev2.companyCode !== companyCode2 || prev2.Job_NID !== jobNID2 || prev2.User_NID !== userNID2 || prev2.codeType !== codeType2 || prev2.evaluation_type !== evaluationType2);
              if (hasExistingAnswers2 && changed2) {
                safeRemove(STORAGE_KEYS.QUIZ_ANSWERS);
                safeRemove(orderKey2);
                safeRemove(STORAGE_KEYS.START_TIME);
                safeRemove(STORAGE_KEYS.TIME_TAKEN);
                sessions2[useruuid] = { ...assessmentMetadata(userInfo), evaluation_uuid, companyCode: companyCode2, Job_NID: jobNID2, User_NID: userNID2, codeType: codeType2, evaluation_type: evaluationType2, userInfo: prev2?.userInfo, createdAt: new Date().toISOString() };
                safeSet(STORAGE_KEYS.USER_EVALUATION_SESSIONS, sessions2);
                navigate(introUrl());
                setLoading(false);
                return;
              }
              sessions2[useruuid] = { ...assessmentMetadata(userInfo), evaluation_uuid, companyCode: companyCode2, Job_NID: jobNID2, User_NID: userNID2, codeType: codeType2, evaluation_type: evaluationType2, userInfo: prev2?.userInfo, createdAt: new Date().toISOString() };
              safeSet(STORAGE_KEYS.USER_EVALUATION_SESSIONS, sessions2);
              navigate(introUrl());
              setLoading(false);
              return;
            }
          }
        }
        
        // 如果API没有找到记录，检查localStorage中是否有未完成的测评会话
        const sessions = safeGet(STORAGE_KEYS.USER_EVALUATION_SESSIONS, {});
        if (sessions[useruuid]) {
          const userSession = sessions[useruuid];
            
            // 再次检查该会话的状态
            try {
              const sessionStatusResponse = await axios.get(`${API_BASE_URL}/api/assessment/evaluation_status`, {
                params: {
                  useruuid: useruuid,
                  evaluation_uuid: userSession.evaluation_uuid
                },
                headers: {
                  Authorization: `Bearer ${accessToken}`,
                  'Content-Type': 'application/json',
                },
              });
              
              if (sessionStatusResponse.status === 200) {
                const sessionStatus = sessionStatusResponse.data.status;
                
                if (sessionStatus === 'complete') {
                  setError('您已经完成过此测评，无法重复进行。如有疑问，请联系管理员。');
                  setLoading(false);
                  return;
                } else if (sessionStatus === 'start' || sessionStatus === 'create') {
                  const urlParams3 = new URLSearchParams(window.location.search);
                  const companyCode3 = urlParams3.get('companyCode') || '';
                  const jobNID3 = urlParams3.get('Job_NID') || '';
                  const userNID3 = urlParams3.get('User_NID') || '';
                  const codeType3 = urlParams3.get('codeType') || '';
                  const evaluationType3 = urlParams3.get('evaluation_type') || '';
                  const userInfo = { useruuid, defaultLang, evaluation_uuid: userSession.evaluation_uuid,
                    ...userSession.userInfo, ...languageMetadata(), ...responseMetadata(sessionStatusResponse.data),
                    ...(getCommonParams().report_language ? { report_language: reportLanguage } : {}) };
                  safeSet(STORAGE_KEYS.USER_INFO, userInfo);
                  safeSet(STORAGE_KEYS.ACTIVE_SESSION, {
                    ...userSession,
                    ...userInfo,
                    companyCode: companyCode3 || userSession.companyCode || '',
                    Job_NID: jobNID3 || userSession.Job_NID || '',
                    User_NID: userNID3 || userSession.User_NID || '',
                    codeType: codeType3 || userSession.codeType || '',
                    evaluation_type: evaluationType3 || userSession.evaluation_type || '',
                    createdAt: userSession.createdAt || new Date().toISOString(),
                  });
                  const orderKey3 = `${STORAGE_KEYS.QUIZ_ORDER_PREFIX}${defaultLang}`;
                  let existingAnswers3 = safeGet(STORAGE_KEYS.QUIZ_ANSWERS, {});
                  const hasExistingAnswers3 = Object.keys(existingAnswers3).length > 0;
                  const prev3 = sessions[useruuid];
                  const changed3 = !!prev3 && (prev3.companyCode !== companyCode3 || prev3.Job_NID !== jobNID3 || prev3.User_NID !== userNID3 || prev3.codeType !== codeType3 || prev3.evaluation_type !== evaluationType3);
                  if (hasExistingAnswers3 && changed3) {
                    safeRemove(STORAGE_KEYS.QUIZ_ANSWERS);
                    safeRemove(orderKey3);
                    safeRemove(STORAGE_KEYS.START_TIME);
                    safeRemove(STORAGE_KEYS.TIME_TAKEN);
                    sessions[useruuid] = { ...assessmentMetadata(userInfo), evaluation_uuid: userSession.evaluation_uuid, companyCode: companyCode3, Job_NID: jobNID3, User_NID: userNID3, codeType: codeType3, evaluation_type: evaluationType3, userInfo: userSession.userInfo, createdAt: new Date().toISOString() };
                    safeSet(STORAGE_KEYS.USER_EVALUATION_SESSIONS, sessions);
                    navigate(introUrl());
                    setLoading(false);
                    return;
                  }
                  sessions[useruuid] = { ...assessmentMetadata(userInfo), evaluation_uuid: userSession.evaluation_uuid, companyCode: companyCode3, Job_NID: jobNID3, User_NID: userNID3, codeType: codeType3, evaluation_type: evaluationType3, userInfo: userSession.userInfo, createdAt: new Date().toISOString() };
                  safeSet(STORAGE_KEYS.USER_EVALUATION_SESSIONS, sessions);
                  navigate(introUrl());
                  setLoading(false);
                  return;
                }
              }
            } catch (sessionError) {
              console.error('检查会话状态时出错:', sessionError);
              // 如果检查失败，清除可能无效的会话记录
              delete sessions[useruuid];
              safeSet(STORAGE_KEYS.USER_EVALUATION_SESSIONS, sessions);
            }
        }

        // 显示表单
        setShowForm(true);
        setLoading(false);
      } catch (error) {
        console.error('检查测评状态时出错:', error);
        
        // 检查是否是认证错误
        if (error && error.response && error.response.status === 401) {
          setError('认证失败，请检查访问权限。');
          setLoading(false);
          return;
        }
        
        // 检查是否是网络连接错误
        if (error && (error.code === 'ECONNREFUSED' || (error.message && error.message.includes('Network Error')))) {
          setError('无法连接到服务器，请检查网络连接或稍后重试。');
          setLoading(false);
          return;
        }
        
        // 如果API调用失败，回退到localStorage检查
        const completedList = safeGet(STORAGE_KEYS.COMPLETED_EVALUATIONS, []);
        if (Array.isArray(completedList) && completedList.includes(useruuid)) {
            setError('您已经完成过此测评，无法重复进行。如有疑问，请联系管理员。');
            setLoading(false);
            return;
        }
        setShowForm(true);
        setLoading(false);
      }
    }

    fetchUserInfo();
  }, [useruuid, defaultLang, evaluation_uuid, isNewCandidate, navigate, accessToken, t.userNotFound, languageMetadata, reportLanguage]);

  // 表单输入处理
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => {
      const newData = { ...prev, [name]: value };
      if (name === 'ageGroup' && type === 'intelligent') {
        newData.birthYear = '';
      }
      return newData;
    });
    
    // 清除对应字段的错误信息
    if (formErrors[name]) {
      setFormErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  // 表单验证
  const validateForm = () => {
    const errors = {};
    
    // 姓名验证（必填）
    if (!formData.name.trim()) {
      errors.name = t.nameRequired;
    }
    
    // 性别验证（必填）
    if (!formData.gender) {
      errors.gender = t.genderRequired;
    }
    
    // 出生年验证（必填）
    if (!formData.birthYear.toString().trim()) {
      errors.birthYear = t.birthYearRequired;
    } else {
      const year = parseInt(formData.birthYear);
      let minYear, maxYear;

      if (type === 'intelligent' && formData.ageGroup) {
        const ageGroupRanges = {
          'kid': { min: 3, max: 6 },
          'elementary': { min: 7, max: 12 },
          'junior': { min: 13, max: 15 },
          'high': { min: 16, max: 18 },
          'university': { min: 19, max: 22 },
          'community': { min: 23, max: 99 }
        };
        const range = ageGroupRanges[formData.ageGroup];
        if (range) {
          minYear = currentYear - range.max;
          maxYear = currentYear - range.min;
        }
      } else {
        const minAge = assessmentConfig.ageRange?.min || 14;
        const maxAge = assessmentConfig.ageRange?.max || 99;
        minYear = currentYear - maxAge;
        maxYear = currentYear - minAge;
      }
      
      if (minYear !== undefined && maxYear !== undefined && (isNaN(year) || year < minYear || year > maxYear)) {
        errors.birthYear = `出生年应在${minYear}-${maxYear}之间`;
      }
    }
    
    // 邮箱验证（非必填，但如果填写了需要格式正确）
    if (formData.email.trim()) {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(formData.email)) {
        errors.email = t.emailRequired;
      }
    }
    
    // 如果是多元智能测评，需要验证年龄段
    if (type === 'intelligent' && !formData.ageGroup) {
      errors.ageGroup = '请选择年龄段';
    }
    
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // 表单提交
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }
    
    setSubmitting(true);
    setError('');
    
    try {
      // 从 URL 参数中获取额外的参数
      const { 
        companyCode,
        Job_NID: jobNID,
        User_NID: userNID,
        codeType,
        evaluation_type: evaluationType
      } = getCommonParams();
      
      // 创建测评会话的API调用 - 使用create_evaluation_session端点
      const e_data = {
        defaultLang,
        ...(isAdultAssessment(type) ? languageMetadata() : {}),
        useruuid,
        username: formData.name,
        useremail: formData.email || '',
        usersex: parseInt(formData.gender),
        year: parseInt(formData.birthYear)
      };

      if (type === 'intelligent') {
        e_data.ageGroup = formData.ageGroup;
      }
      
      // 添加 URL 参数到 e_data（如果存在）
      if (companyCode) {
        e_data.companyCode = companyCode;
      }
      if (jobNID) {
        e_data.Job_NID = jobNID;
      }
      if (userNID) {
        e_data.User_NID = userNID;
      }
      if (codeType) {
        e_data.codeType = codeType;
      }
      if (evaluationType) {
        e_data.evaluation_type = evaluationType;
      } else {
        e_data.evaluation_type = type;
      }
      

      
      // 使用 create_evaluation_session 端点创建新的测评会话
      const response = await axios.post(`${API_BASE_URL}/api/assessment/create_evaluation_session`, e_data, {
        headers: {
          Authorization: `Bearer ${accessToken}`,
          'Content-Type': 'application/json',
        },
      });
      
      const returnedEvaluationUuid = response.data?.evaluation_uuid;
      if (response.status === 200 && returnedEvaluationUuid) {
        // 成功创建测评会话，保存用户信息和evaluation_uuid
        // 注意：不再保存companyCode、Job_NID、codeType到localStorage，这些参数只从URL获取
        const userInfo = { 
          useruuid, 
          defaultLang, 
          ...(isAdultAssessment(type) ? { ...languageMetadata(), ...responseMetadata(response.data) } : {}),
          evaluation_uuid: returnedEvaluationUuid,
          name: formData.name,
          email: formData.email || '',
          phone: formData.phone || '',
          birthYear: parseInt(formData.birthYear),
          gender: parseInt(formData.gender),
          ...(type === 'intelligent' && { ageGroup: formData.ageGroup })
        };
        if (isAdultAssessment(type)) persistAssessmentMetadata(userInfo, assessmentMetadata(userInfo));
        
        safeSet(STORAGE_KEYS.USER_INFO, userInfo);
        safeSet(STORAGE_KEYS.ACTIVE_SESSION, {
          ...userInfo,
          companyCode: companyCode || '',
          Job_NID: jobNID || '',
          User_NID: userNID || '',
          codeType: codeType || '',
          evaluation_type: evaluationType || type,
          userInfo: {
            name: formData.name,
            email: formData.email || '',
            phone: formData.phone || '',
            birthYear: parseInt(formData.birthYear),
            gender: parseInt(formData.gender),
          },
          createdAt: new Date().toISOString(),
        });
        
        // 保存会话信息，用于下次检查
        const sessions = safeGet(STORAGE_KEYS.USER_EVALUATION_SESSIONS, {});
        sessions[useruuid] = {
          ...assessmentMetadata(userInfo),
          evaluation_uuid: returnedEvaluationUuid,
          companyCode: companyCode || '',
          Job_NID: jobNID || '',
          User_NID: userNID || '',
          codeType: codeType || '',
          evaluation_type: evaluationType || type,
          userInfo: {
            name: formData.name,
            email: formData.email || '',
            phone: formData.phone || '',
            birthYear: parseInt(formData.birthYear),
            gender: parseInt(formData.gender),
            ...(type === 'intelligent' && { ageGroup: formData.ageGroup })
          },
          createdAt: new Date().toISOString()
        };
        safeSet(STORAGE_KEYS.USER_EVALUATION_SESSIONS, sessions);
        

        
        navigate(introUrl());
      } else {
        setError('创建测评会话失败，请稍后重试');
      }
    } catch (err) {
      
      // 检查是否是因为测评已存在或已完成的错误
      if (err.response && err.response.data) {
        const errorMessage = err.response.data.message || err.response.data.error || '';
        
        if (errorMessage.includes('已存在') || errorMessage.includes('已完成') || 
            errorMessage.includes('already exists') || errorMessage.includes('completed')) {
          setError('您已经完成过此测评，无法重复进行。如有疑问，请联系管理员。');
        } else {
          setError(errorMessage || '创建测评记录失败，请稍后重试');
        }
      } else {
        const errorMessage = err.message || '创建测评记录失败，请稍后重试';
        setError(errorMessage);
      }
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) return <div>{t.loading || 'loading...'}</div>;
  if (error && !showForm) return <div style={{ color: 'red', padding: '20px' }}>{error}</div>;

  // 如果需要显示表单
  if (showForm) {
    return (
      <div className="user-info-form">
        <h1>{isAdultAssessment(type) ? t.title : (assessmentConfig.title || t.title)}</h1>
        <p>{t.description}</p>
        
        {error && <div style={{ color: 'red', marginBottom: '20px' }}>{error}</div>}
        
        <form onSubmit={handleSubmit} className="form-container">
          {isAdultAssessment(type) && <div className="form-row">
            <div className="form-group half-width">
              <label htmlFor="assessmentLanguage" className="form-label">{t.assessmentLanguage}</label>
              <select id="assessmentLanguage" className="form-field" value={answerLanguage}
                onChange={event => setAnswerLanguage(event.target.value)}>
                {LANGUAGE_OPTIONS.map(([value, label]) => <option key={value} value={value}>{label}</option>)}
              </select>
            </div>
            <div className="form-group half-width">
              <label htmlFor="reportLanguage" className="form-label">{t.reportLanguage}</label>
              <select id="reportLanguage" className="form-field" value={reportLanguage}
                onChange={event => setReportLanguage(event.target.value)}>
                {LANGUAGE_OPTIONS.map(([value, label]) => <option key={value} value={value}>{label}</option>)}
              </select>
            </div>
          </div>}
          {/* 第一行：姓名和 (如果是智能版：年龄段；如果不是：出生年) */}
          <div className="form-row">
            <div className="form-group half-width">
              <label htmlFor="name" className="form-label">{t.name} *</label>
              <input
                type="text"
                id="name"
                name="name"
                value={formData.name}
                onChange={handleInputChange}
                className={`form-field ${formErrors.name ? 'is-invalid' : ''}`}
                placeholder={`${t.pleaseInput} ${t.name}`}
              />
              {formErrors.name && <div className="error-message">{formErrors.name}</div>}
            </div>

            {type === 'intelligent' ? (
              <div className="form-group half-width">
                <label htmlFor="ageGroup" className="form-label">年龄段 *</label>
                <select
                  id="ageGroup"
                  name="ageGroup"
                  value={formData.ageGroup}
                  onChange={handleInputChange}
                  className={`form-field ${formErrors.ageGroup ? 'is-invalid' : ''}`}
                >
                  <option value="">{t.pleaseSelect || '请选择'}</option>
                  <option value="kid">幼儿</option>
                  <option value="elementary">小学</option>
                  <option value="junior">初中</option>
                  <option value="high">高中</option>
                  <option value="university">大学</option>
                  <option value="community">职场</option>
                </select>
                {formErrors.ageGroup && <div className="error-message">{formErrors.ageGroup}</div>}
              </div>
            ) : (
              <div className="form-group half-width">
                <label htmlFor="birthYear" className="form-label">{t.birthYear} *</label>
                <select
                  id="birthYear"
                  name="birthYear"
                  value={formData.birthYear}
                  onChange={handleInputChange}
                  className={`form-field ${formErrors.birthYear ? 'is-invalid' : ''}`}
                >
                  <option value="">{t.pleaseSelect || '请选择'}</option>
                  {yearOptions.map(year => (
                    <option key={year} value={year}>{year}</option>
                  ))}
                </select>
                {formErrors.birthYear && <div className="error-message">{formErrors.birthYear}</div>}
              </div>
            )}
          </div>
          
          {/* 第二行：性别 和 (如果是智能版：出生年；如果不是：空) */}
          <div className="form-row">
            <div className={`form-group ${type === 'intelligent' ? 'half-width' : ''}`}>
              <label className="form-label">{t.gender} *</label>
              <div className="gender-options">
                <label className={`gender-card ${formData.gender === '1' ? 'selected' : ''}`}>
                  <input
                    type="radio"
                    name="gender"
                    value="1"
                    checked={formData.gender === '1'}
                    onChange={handleInputChange}
                    className="gender-input"
                  />
                  <span className="gender-text">{t.male}</span>
                </label>
                <label className={`gender-card ${formData.gender === '0' ? 'selected' : ''}`}>
                  <input
                    type="radio"
                    name="gender"
                    value="0"
                    checked={formData.gender === '0'}
                    onChange={handleInputChange}
                    className="gender-input"
                  />
                  <span className="gender-text">{t.female}</span>
                </label>
              </div>
              {formErrors.gender && <div className="error-message">{formErrors.gender}</div>}
            </div>

            {type === 'intelligent' && (
              <div className="form-group half-width">
                <label htmlFor="birthYear" className="form-label">{t.birthYear} *</label>
                <select
                  id="birthYear"
                  name="birthYear"
                  value={formData.birthYear}
                  onChange={handleInputChange}
                  className={`form-field ${formErrors.birthYear ? 'is-invalid' : ''}`}
                  disabled={!formData.ageGroup}
                >
                  <option value="">{formData.ageGroup ? (t.pleaseSelect || '请选择') : '请先选择年龄段'}</option>
                  {yearOptions.map(year => (
                    <option key={year} value={year}>{year}</option>
                  ))}
                </select>
                {formErrors.birthYear && <div className="error-message">{formErrors.birthYear}</div>}
              </div>
            )}
          </div>
          
          {/* 第三行：邮箱和电话 */}
          <div className="form-row">
            <div className="form-group half-width">
              <label htmlFor="email" className="form-label">{t.email}</label>
              <input
                type="email"
                id="email"
                name="email"
                value={formData.email}
                onChange={handleInputChange}
                className={`form-field ${formErrors.email ? 'is-invalid' : ''}`}
                placeholder={`${t.pleaseInput} ${t.email}（${t.optional}）`}
              />
              {formErrors.email && <div className="error-message">{formErrors.email}</div>}
            </div>

            <div className="form-group half-width">
              <label htmlFor="phone" className="form-label">{t.phone}</label>
              <input
                type="tel"
                id="phone"
                name="phone"
                value={formData.phone}
                onChange={handleInputChange}
                className="form-field"
                placeholder={`${t.pleaseInput} ${t.phone}（${t.optional}）`}
              />
            </div>
          </div>
          
          <button 
            type="submit" 
            className="submit-button"
            disabled={submitting}
          >
            {submitting ? 'submitting...' : t.next}
          </button>
        </form>
      </div>
    );
  }

  return null; // 沒有 useruuid 而不顯示表單，也不跳轉
}

export default UserInfoForm;
